import { CookieKeyMap } from '../constants/cookie';
import { appendContextCookies } from './cookie/appendContextCookies';
import { createSetCookieString } from './cookie/createSetCookieString';
import { expireSetCookieString } from './cookie/expireSetCookieString';

import type { GetServerSidePropsContext } from 'next';
import type { RequireParams, ServiceAppState } from '@kc-console/context';

interface UpdateGuardsCookieParams {
  context: GetServerSidePropsContext;
  state: Partial<ServiceAppState>;
}

/**
 * 가드 결과를 쿠키에 업데이트합니다.
 * @description
 * - redirect가 있는 경우 쿼리용 쿠키를 리셋합니다.
 * - redirect가 없는 경우 쿼리용 쿠키(projectId, domainId, region)를 저장합니다.
 * - token이 있는 경우 token을 쿠키에 저장합니다.
 * - language가 있는 경우 language를 쿠키에 저장합니다.
 */
export const updateGuardsCookie = ({ context, state }: UpdateGuardsCookieParams) => {
  const { host } = context.req.headers;
  const domain = `.${host?.split('.')?.[1]}.com`;
  const newCookies: string[] = [];
  /**
   * redirect 유무에 따라 QUERY_KEY 쿠키를 저장 혹은 리셋합니다.
   */
  if (state.redirect) {
    newCookies.push(
      expireSetCookieString(CookieKeyMap.QUERY_KEY, {
        secure: true,
      }),
    );
  } else {
    const requireQueryParams: RequireParams = {
      projectId: state.projectId,
      domainId: state.domainId || '',
      region: state.region,
    };
    newCookies.push(
      createSetCookieString(CookieKeyMap.QUERY_KEY, JSON.stringify(requireQueryParams), {
        domain,
      }),
    );
  }

  /**
   * token을 쿠키에 저장합니다.
   */
  if (state.token) {
    newCookies.push(
      createSetCookieString(CookieKeyMap.TOKEN_KEY, state.token, {
        domain,
      }),
    );
  }

  /**
   * language를 쿠키에 저장합니다.
   */
  if (state.language) {
    newCookies.push(
      createSetCookieString(CookieKeyMap.LANGUAGE_KEY, state.language, {
        domain,
        secure: true,
        httpOnly: false,
      }),
    );
  }

  /**
   * displayLanguage를 쿠키에 저장합니다.
   */
  newCookies.push(
    createSetCookieString(CookieKeyMap.DISPLAY_LANGUAGE_KEY, state.displayLanguage || '', {
      domain,
    }),
  );

  appendContextCookies(context, newCookies);
};
